/**
 * Domino model. A standard "double-six" set is 28 tiles — every unordered
 * pair of pip values 0..6 (including doubles). Each tile has a `low` and
 * `high` half (low ≤ high). Tile id is canonical: `d-{low}-{high}`.
 *
 * "Most basic" dominoes uses this set; double-nine (55 tiles) and
 * double-twelve (91) variants exist but aren't shipped.
 */

export interface Domino {
  id: string;
  low: number;
  high: number;
}

export const MAX_PIP = 6;

export function makeDomino(a: number, b: number): Domino {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  return { id: `d-${low}-${high}`, low, high };
}

export function createDoubleSixSet(): Domino[] {
  const out: Domino[] = [];
  for (let a = 0; a <= MAX_PIP; a++) {
    for (let b = a; b <= MAX_PIP; b++) {
      out.push(makeDomino(a, b));
    }
  }
  return out;
}

export function isDouble(d: Domino): boolean {
  return d.low === d.high;
}

export function pipTotal(d: Domino): number {
  return d.low + d.high;
}

export function handPipTotal(hand: readonly Domino[]): number {
  let s = 0;
  for (const d of hand) s += pipTotal(d);
  return s;
}

/** True if either half of `d` matches `pip`. */
export function matchesPip(d: Domino, pip: number): boolean {
  return d.low === pip || d.high === pip;
}

/** Returns the "other" half — the pip that becomes the new open end when
 *  this tile is played onto an end with value `against`. For doubles, the
 *  open end stays the same value. */
export function otherHalf(d: Domino, against: number): number {
  if (d.low === against) return d.high;
  if (d.high === against) return d.low;
  throw new Error(`domino ${d.id} does not match pip ${against}`);
}
